import classNames from 'classnames';
import React, { ReactNode, useRef, useState } from 'react';
import { Button } from '../actions/Button';
import { PanelHeader } from '../designer/panels/PanelHeader';
import * as Icon from '../icons';
import { Container } from './ContainerFlex';

export type CompareViewProps = {
  firstContent: ReactNode;
  secondContent: ReactNode;
  layoutToggle?: boolean;
  initialIsVertical?: boolean;
  showFirst?: boolean;
  showSecond?: boolean;
  containerClassName?: string;
  firstClassName?: string;
  secondClassName?: string;
  dividerClassName?: string;
  firstContentHeader?: ReactNode;
  secondContentHeader?: ReactNode;
  firstContentFooter?: ReactNode;
  secondContentFooter?: ReactNode;
};

const CompareView: React.FC<CompareViewProps> = ({
  firstContent,
  secondContent,
  layoutToggle = true,
  initialIsVertical = false,
  showFirst = true,
  showSecond = true,
  containerClassName = '',
  firstClassName = '',
  secondClassName = '',
  dividerClassName = 'border-app-panel-dark',
  firstContentHeader,
  secondContentHeader,
  firstContentFooter,
  secondContentFooter,
}) => {
  const [isVertical, setIsVertical] = useState(initialIsVertical);
  const firstContentRef = useRef<HTMLDivElement>(null);
  const secondContentRef = useRef<HTMLDivElement>(null);

  // Single view mode
  if (showFirst && !showSecond) {
    return (
      <Container
        col
        className={classNames('relative flex-1', containerClassName)}
      >
        <PanelHeader
          title={<div className="flex items-center">{firstContentHeader}</div>}
        />
        <Container
          col
          flex1
          className={classNames('relative', firstClassName)}
          ref={firstContentRef}
        >
          {firstContent}
        </Container>
        {firstContentFooter}
      </Container>
    );
  }

  if (!showFirst && showSecond) {
    return (
      <Container
        col
        className={classNames('relative flex-1', containerClassName)}
      >
        <PanelHeader
          title={<div className="flex items-center">{secondContentHeader}</div>}
        />
        <Container
          col
          flex1
          className={classNames('relative', secondClassName)}
          ref={secondContentRef}
        >
          {secondContent}
        </Container>
        {secondContentFooter}
      </Container>
    );
  }

  // Compare view mode
  const toggleButton = layoutToggle && (
    <Button
      buttonSize="sm"
      buttonType="secondary"
      hasIconAfter={
        <Icon.ScreenViewDual
          className={classNames(
            'h-5 w-5 fill-current',
            isVertical ? 'rotate-90' : '',
          )}
        />
      }
      hasIcon
      onPress={() => setIsVertical(!isVertical)}
      className="rounded shadow-md"
      //   label={layoutToggleButtonLabel}
    />
  );

  return (
    <Container
      col={isVertical}
      className={classNames('relative mx-0.5 flex-1', containerClassName)}
    >
      <Container
        col
        flex1
        className={classNames(
          'relative',
          firstClassName,
          isVertical
            ? `border-b-4 ${dividerClassName}`
            : `ltr:border-r-4 rtl:border-l-4 ${dividerClassName}`,
        )}
        ref={firstContentRef}
      >
        <PanelHeader
          title={
            <div className="flex items-center">
              {toggleButton}
              {firstContentHeader}
            </div>
          }
        />
        <Container className="relative" col>
          {firstContent}
        </Container>

        {firstContentFooter}
      </Container>
      <Container
        col
        flex1
        className={classNames(
          'relative',
          secondClassName,
          isVertical ? '' : `ltr:border-l rtl:border-r ${dividerClassName}`,
        )}
        ref={secondContentRef}
      >
        <PanelHeader title={secondContentHeader} />
        <Container className="relative" col>
          {secondContent}
        </Container>
      </Container>
      {secondContentFooter}
    </Container>
  );
};

export default CompareView;
